import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./icons";

export function EmptyAction({ children, onClick, to }) {
  const className = "mt-5 inline-flex h-10 items-center gap-1.5 rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white transition duration-200 hover:bg-[#c4631d]";
  if (to) return <Link to={to} className={className}>{children}</Link>;
  return <button type="button" className={className} onClick={onClick}>{children}</button>;
}

export function EmptyState({ icon = "visits", title, body, action, compact = false, plain = false }) {
  const frame = plain || compact ? "px-6 py-12" : "rounded-xl border border-slate-200 bg-white px-6 py-16";
  return (
    <div className={`flex flex-col items-center justify-center text-center ${compact ? "flex-1" : ""} ${frame}`}>
      <span className="relative mb-4 flex h-16 w-16 items-center justify-center">
        <span className="absolute inset-0 rounded-2xl bg-sky-50" />
        <span className="absolute -right-1 -top-1 h-5 w-5 rounded-full bg-brand-orange/20" />
        <Icon name={icon} className="relative h-7 w-7 text-brand-blue" />
      </span>
      <p className="text-base font-extrabold tracking-tight text-brand-ink">{title}</p>
      {body ? <p className="mt-1.5 max-w-xs text-sm leading-6 text-slate-500">{body}</p> : null}
      {action}
    </div>
  );
}

export function SlideForm({ open, title, description, onClose, children }) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    panelRef.current?.querySelector("input, select, textarea")?.focus();
    function onKey(event) {
      if (event.key === "Escape") onCloseRef.current();
    }
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open]);

  return (
    <div className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`}>
      <button type="button" tabIndex={open ? 0 : -1} className={`absolute inset-0 bg-[#0B1C33]/40 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`} aria-label="Close form" onClick={onClose} />
      <div ref={panelRef} role="dialog" aria-modal={open} aria-hidden={!open} aria-labelledby="slide-form-title" className={`absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-xl transition-transform duration-200 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-slate-100 px-5">
          <h2 id="slide-form-title" className="text-base font-semibold text-brand-ink">{title}</h2>
          <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-brand-ink" aria-label="Close" onClick={onClose}>
            <Icon name="close" className="h-5 w-5" />
          </button>
        </div>
        {description ? <p className="px-5 pt-4 text-sm leading-6 text-slate-500">{description}</p> : null}
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </div>
    </div>
  );
}

export function PageHeader({ title, description, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-brand-ink">{title}</h1>
        {description ? <p className="mt-1 max-w-2xl text-sm text-slate-600">{description}</p> : null}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}

export function Stat({ label, value, detail }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-brand-ink">{value}</p>
      {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
    </div>
  );
}

export function Badge({ children }) {
  return <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700">{children}</span>;
}

export function Notice({ tone = "error", children }) {
  const styles = tone === "error" ? "border-rose-200 bg-rose-50 text-rose-900" : "border-sky-200 bg-sky-50 text-sky-900";
  return <p className={`rounded border px-3 py-2 text-sm ${styles}`}>{children}</p>;
}

export function DataTable({ columns, rows, empty, loading, embedded = false }) {
  const shell = embedded ? "bg-white" : "overflow-hidden rounded-xl border border-slate-200 bg-white";
  if (loading) {
    return (
      <div className={shell} aria-busy="true">
        <div className="bg-slate-50 px-3 py-3"><div className="h-3 w-32 animate-pulse rounded bg-slate-200" /></div>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="flex gap-6 border-t border-slate-100 px-3 py-3">
            <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-24 animate-pulse rounded bg-slate-100" />
          </div>
        ))}
      </div>
    );
  }
  if (!rows.length) {
    const content = typeof empty === "string" ? { title: empty } : empty;
    return <EmptyState plain={embedded} icon={content?.icon} title={content?.title || "Nothing here yet"} body={content?.body} action={content?.action} />;
  }
  return (
    <div className={`overflow-x-auto ${embedded ? "bg-white" : "rounded-xl border border-slate-200 bg-white"}`}>
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>{columns.map((column) => <th key={column.key} className="px-4 py-3 font-semibold">{column.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || index} className="border-t border-slate-100 hover:bg-slate-50/80">
              {columns.map((column) => <td key={column.key} className="px-4 py-3 align-middle">{column.render ? column.render(row) : (row[column.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Unavailable({ title, reason, icon = "audit" }) {
  return (
    <div>
      <PageHeader title={title} />
      <EmptyState icon={icon} title="Not available yet" body={reason} />
    </div>
  );
}
